import type { FindingAnchor } from "../shared/schemas";

/**
 * Anchoring a finding to a place on the page.
 *
 * Ported from qa-deck's `src/widget/anchor.ts` (itself derived from
 * zuperinvoice's collaboration anchors), with one substantial addition forced
 * by the SaaS move: **a customer's site has no `data-ref` convention.**
 *
 * qa-deck could always find a nearby `[data-ref]` because the one product it
 * served annotated its components for Playwright. A generic tenant has not, so
 * anchoring now stores THREE locators and lets the reader pick:
 *
 *  - `anchorRef` — nearest `[data-ref]`, when the site happens to have them
 *    (still the most human-readable, still NOT unique: values repeat);
 *  - `selector`  — a generated CSS-selector path, which always exists (unique
 *    on capture, but brittle across markup changes);
 *  - `x`/`y`     — the raw viewport point, which disambiguates the first and
 *    survives when the second stops matching.
 *
 * Dogfooding note: this app's own e2e convention IS `data-ref`, so pointing
 * qadeck at qadeck still produces the rich anchors.
 */

export const CONTAINER_ANCHOR = "__container__";

/** Overlays that sit on top of everything and would always win on distance. */
const SKIP_REF_PREFIXES = ["Collaboration:", "ClickToComment:", "QaDeck:"];

/** Marks the widget's own light-DOM nodes (host element + pick overlays). */
export const CHROME_ATTR = "data-qa-deck-chrome";

export function isWidgetChrome(el: Element | null): boolean {
  return (
    !!el &&
    typeof el.closest === "function" &&
    el.closest(`[${CHROME_ATTR}]`) !== null
  );
}

export function anchorContainer(): HTMLElement {
  return document.body;
}

export function findAnchorAtPoint(
  clientX: number,
  clientY: number,
  container: HTMLElement = anchorContainer(),
): { anchorRef: string; element: HTMLElement } | null {
  const MAX_DIST = 200; // px — max distance to look for an anchor
  const refElements = container.querySelectorAll("[data-ref]");
  let best: { anchorRef: string; element: HTMLElement } | null = null;
  let bestScore = Infinity;

  for (const refEl of refElements) {
    const ref = (refEl as HTMLElement).getAttribute("data-ref") || "";
    if (SKIP_REF_PREFIXES.some((p) => ref.startsWith(p))) continue;
    if (isWidgetChrome(refEl)) continue;

    const rect = refEl.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) continue;

    // Distance from the point to the nearest edge of the bounding box
    const dx = Math.max(rect.left - clientX, 0, clientX - rect.right);
    const dy = Math.max(rect.top - clientY, 0, clientY - rect.bottom);
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > MAX_DIST) continue;

    // Score: distance + area penalty so small specific elements beat containers
    const area = rect.width * rect.height;
    const areaPenalty = Math.log10(Math.max(area, 1)) * 30;
    const score = dist + areaPenalty;

    if (score < bestScore) {
      bestScore = score;
      best = { anchorRef: ref, element: refEl as HTMLElement };
    }
  }

  return best;
}

/** CSS.escape is universally available in the widget's browser targets, but a
 *  hand-rolled fallback keeps a missing polyfill from breaking capture. */
function escapeIdent(value: string): string {
  const css = (
    window as unknown as { CSS?: { escape?: (s: string) => string } }
  ).CSS;
  if (css?.escape) return css.escape(value);
  return value.replace(/([^\w-])/g, "\\$1");
}

/**
 * A CSS path from `<body>` down to `el`.
 *
 * Preference order per level: a stable id → a `data-ref`/`data-testid` → a tag
 * plus its `:nth-of-type` position. Classes are deliberately NOT used: on a
 * Tailwind or CSS-modules site they are either hashed per build or so numerous
 * that the selector becomes noise, and either way they change more often than
 * structure does.
 *
 * The result is a best-effort locator, not a guarantee. It is stored alongside
 * the viewport point precisely because it can go stale.
 */
export function cssSelectorPath(el: Element): string {
  const parts: string[] = [];
  let node: Element | null = el;

  while (node && node.nodeType === 1 && node !== document.body) {
    const id = node.getAttribute("id");
    // An id is only useful if it is unique AND looks authored rather than
    // generated (React/Radix ids churn on every render).
    if (id && !/[:_]|^r[0-9a-z]+$/i.test(id)) {
      try {
        if (document.querySelectorAll(`#${escapeIdent(id)}`).length === 1) {
          parts.unshift(`#${escapeIdent(id)}`);
          break;
        }
      } catch {
        /* invalid id for a selector — fall through */
      }
    }

    const testHook =
      node.getAttribute("data-ref") ?? node.getAttribute("data-testid");
    if (testHook) {
      parts.unshift(
        `${node.tagName.toLowerCase()}[data-ref="${testHook.replace(/"/g, '\\"')}"]`,
      );
    } else {
      const tag = node.tagName.toLowerCase();
      const parent: Element | null = node.parentElement;
      if (!parent) {
        parts.unshift(tag);
      } else {
        const siblings = Array.from(parent.children).filter(
          (c) => c.tagName === node!.tagName,
        );
        parts.unshift(
          siblings.length > 1
            ? `${tag}:nth-of-type(${siblings.indexOf(node) + 1})`
            : tag,
        );
      }
    }

    node = node.parentElement;
  }

  return ["body", ...parts].join(" > ");
}

/**
 * Build the anchor for a picked element + the point that was clicked.
 *
 * `element` is what the picker highlighted; the point may sit slightly outside
 * it (the picker allows a near miss), which is why the offset is stored
 * relative to the element rather than recomputed from the point later.
 */
export function computeAnchorForPoint(
  clientX: number,
  clientY: number,
  element: Element | null,
  container: HTMLElement = anchorContainer(),
): FindingAnchor {
  const selector = element ? cssSelectorPath(element) : "body";
  const found = findAnchorAtPoint(clientX, clientY, container);

  if (found) {
    const rect = found.element.getBoundingClientRect();
    return {
      anchorRef: found.anchorRef,
      selector,
      anchorOffsetX: Math.round(clientX - rect.left),
      anchorOffsetY: Math.round(clientY - rect.top),
      x: Math.round(clientX),
      y: Math.round(clientY),
    };
  }

  // No `data-ref` anywhere near — the common case on a customer site. Offsets
  // become relative to the picked element when there is one, so a pin still
  // tracks that element; otherwise they fall back to the container.
  if (element) {
    const rect = element.getBoundingClientRect();
    return {
      anchorRef: null,
      selector,
      anchorOffsetX: Math.round(clientX - rect.left),
      anchorOffsetY: Math.round(clientY - rect.top),
      x: Math.round(clientX),
      y: Math.round(clientY),
    };
  }

  const containerRect = container.getBoundingClientRect();
  return {
    anchorRef: CONTAINER_ANCHOR,
    selector: "body",
    anchorOffsetX: Math.round(
      clientX - containerRect.left + container.scrollLeft,
    ),
    anchorOffsetY: Math.round(
      clientY - containerRect.top + container.scrollTop,
    ),
    x: Math.round(clientX),
    y: Math.round(clientY),
  };
}

/**
 * Resolve an anchor back to viewport coordinates (used to draw a pin on a
 * previously-reported finding). Tries the `data-ref` first, then the selector
 * path; returns null when neither still matches, so a stale pin is simply not
 * drawn rather than drawn in the wrong place.
 */
export function resolveAnchorToClient(
  anchor: FindingAnchor,
  container: HTMLElement = anchorContainer(),
): { x: number; y: number } | null {
  if (anchor.anchorRef === CONTAINER_ANCHOR) {
    const rect = container.getBoundingClientRect();
    return {
      x: rect.left + anchor.anchorOffsetX - container.scrollLeft,
      y: rect.top + anchor.anchorOffsetY - container.scrollTop,
    };
  }

  let el: Element | null = null;
  if (anchor.anchorRef) {
    const escaped = anchor.anchorRef
      .replace(/\\/g, "\\\\")
      .replace(/"/g, '\\"');
    el = container.querySelector(`[data-ref="${escaped}"]`);
  }
  if (!el && anchor.selector) {
    try {
      el = document.querySelector(anchor.selector);
    } catch {
      el = null; // a selector that no longer parses is just a miss
    }
  }
  if (!el) return null;

  const rect = el.getBoundingClientRect();
  return {
    x: rect.left + anchor.anchorOffsetX,
    y: rect.top + anchor.anchorOffsetY,
  };
}
